/**
 * CAD API Service
 *
 * Handles CAD model upload, listing, viewing, and AI analysis.
 */

import { api, uploadFile } from './api';
import { mapModelResponse } from './cad.helpers';
import {
  CADModel,
  CADClassification,
  CADModelListResponse,
  CADAIAnalysisResponse,
  CADComparisonSummary,
} from '../types/cad';

const CAD_BASE_URL = '/cad';
export const CAD_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

// ==============================================================================
// API Functions
// ==============================================================================

/**
 * Upload a CAD file for processing
 */
export const uploadCADFile = async (
  file: File,
  onProgress?: (progress: number) => void,
  classification?: CADClassification,
): Promise<CADModel> => {
  const data = await uploadFile(
    `${CAD_BASE_URL}/upload`,
    file,
    onProgress,
    CAD_UPLOAD_TIMEOUT_MS,
    classification ? {
      folder_path: classification.folderPath,
      tags: JSON.stringify(classification.tags),
      machine_model: classification.machineModel,
      management_number: classification.managementNumber,
    } : undefined,
  );
  return mapModelResponse(data);
};

/**
 * List all uploaded CAD models
 */
export const listCADModels = async (): Promise<CADModelListResponse> => {
  const data = await api.get<any>(`${CAD_BASE_URL}/models`);
  return {
    models: (data.models || []).map(mapModelResponse),
    total: data.total || 0,
  };
};

/**
 * Get a specific CAD model by ID
 */
export const getCADModel = async (modelId: string): Promise<CADModel> => {
  const data = await api.get<any>(`${CAD_BASE_URL}/models/${modelId}`);
  return mapModelResponse(data);
};

export const updateCADClassification = async (
  model: CADModel,
  classification: CADClassification,
): Promise<CADModel> => {
  const data = await api.patch<any>(`${CAD_BASE_URL}/models/${model.id}/classification`, {
    folder_path: classification.folderPath,
    tags: classification.tags,
    machine_model: classification.machineModel,
    management_number: classification.managementNumber,
    expected_updated_at: model.updatedAt,
  });
  return mapModelResponse(data);
};

/**
 * Get the raw GLB endpoint URL (internal use only).
 */
const getGLBEndpointUrl = (modelId: string): string => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  return `${baseURL}${CAD_BASE_URL}/models/${modelId}/glb`;
};

/** URL prefix shared by every GLB of one model: the base model and its thickness renders. */
export const getModelGlbPrefix = (modelId: string): string => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  return `${baseURL}${CAD_BASE_URL}/models/${modelId}/`;
};

/**
 * Get the GLB file URL for 3D viewing.
 */
export const getGLBUrl = (modelId: string): string => {
  return getGLBEndpointUrl(modelId);
};

/**
 * Run AI analysis on a CAD model
 */
export const analyzeCADModel = async (
  modelId: string
): Promise<CADAIAnalysisResponse> => {
  const data = await api.post<any>(`${CAD_BASE_URL}/models/${modelId}/analyze`, undefined, {
    timeout: 120000, // 2 minutes - GPT-4 analysis takes time
  });
  return {
    modelId: data.model_id,
    analysis: data.analysis,
  };
};

/**
 * Delete a CAD model
 */
export const deleteCADModel = async (modelId: string): Promise<void> => {
  await api.delete(`${CAD_BASE_URL}/models/${modelId}`);
};

/**
 * Compare two CAD models
 */
export const compareCADModels = async (
  modelIdA: string,
  modelIdB: string
): Promise<CADComparisonSummary> => {
  const data = await api.post<any>(`${CAD_BASE_URL}/compare`, {
    model_id_a: modelIdA,
    model_id_b: modelIdB,
  });
  return {
    modelA: data.model_a,
    modelB: data.model_b,
    confidence: data.confidence,
    blockers: data.blockers || [],
    scalarDiffs: data.scalar_diffs || {},
    locationDiffs: (data.location_diffs || []).map((ld: any) => ({
      kind: ld.kind,
      axis: ld.axis,
      before: ld.before,
      after: ld.after,
      significance: ld.significance,
      note: ld.note,
    })),
    reportMarkdown: data.report_markdown || '',
  };
};

// Thickness analysis endpoints live in a sibling module; re-export for callers.
export {
  CAD_REPORT_TIMEOUT_MS,
  createThicknessAnalysis,
  listThicknessAnalyses,
  getThicknessAnalysis,
  getThicknessGLBUrl,
  downloadThicknessReport,
  downloadThicknessPdf,
  recoverThicknessAnalysis,
  getThicknessReview,
  updateThicknessReview,
  createThicknessBatch,
  listThicknessBatches,
  getThicknessBatch,
  updateThicknessBatchClassification,
  retryThicknessBatchItem,
  reanalyzeThicknessBatchItem,
  downloadThicknessBatchZip,
  listThicknessPresets,
  createThicknessPreset,
  compareThicknessAnalyses,
} from './cad.thickness.service';
